import React, { useState, useEffect, useCallback } from 'react';

// Format date as YYYY-MM
const formatYearMonth = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

// Parse YYYY-MM to Date object
const parseYearMonth = (yearMonth) => {
  const [year, month] = yearMonth.split('-').map(num => parseInt(num, 10));
  const date = new Date(year, month - 1, 1);
  return date;
};

// Get the previous month in YYYY-MM format
const getPreviousMonth = (yearMonth) => {
  const date = parseYearMonth(yearMonth);
  date.setMonth(date.getMonth() - 1);
  return formatYearMonth(date);
};

// Get the next month in YYYY-MM format
const getNextMonth = (yearMonth) => {
  const date = parseYearMonth(yearMonth);
  date.setMonth(date.getMonth() + 1);
  return formatYearMonth(date);
};

// Check if today is the first day of the month
const isFirstDayOfMonth = () => {
  const today = new Date();
  return today.getDate() === 1;
};

// Debugging function to log cache contents
const logCacheContents = (dataCache) => {
  console.log('Current cache contents:');
  Object.entries(dataCache).forEach(([period, data]) => {
    console.log(`Period ${period}:`, {
      hasEnergyControl: !!data.energycontrol,
      hasAirControl: !!data.aircontrol,
      energyControlKeys: data.energycontrol ? Object.keys(data.energycontrol) : [],
      airControlKeys: data.aircontrol ? Object.keys(data.aircontrol) : []
    });
  });
};

// Debug util function to help track what's happening
const inspectData = (label, data) => {
  console.log(`${label}:`, {
    hasData: !!data,
    keys: data ? Object.keys(data) : [],
    hasEnergyControl: data && !!data.energycontrol,
    hasAirControl: data && !!data.aircontrol,
    energyControlKeys: data && data.energycontrol ? Object.keys(data.energycontrol) : [],
    airControlKeys: data && data.aircontrol ? Object.keys(data.aircontrol) : []
  });
};



// Initialize the app by checking and possibly fixing cache issues
const initializeApp = () => {
  console.log("Initializing app and checking cache integrity...");
  
  // First, let's examine the sessionStorage to check for cache issues
  const cacheEntries = [];
  const issuesFound = [];
  
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key && key.startsWith('monthlyData_') && key !== 'monthlyData_lastCacheTime') {
      try {
        const period = key.replace('monthlyData_', '');
        const cachedData = JSON.parse(sessionStorage.getItem(key));
        
        cacheEntries.push({ period, data: cachedData });
        
        // Check for issues
        if (cachedData.energycontrol && !cachedData.aircontrol) {
          issuesFound.push({ period, issue: 'missing_aircontrol' });
        } else if (!cachedData.energycontrol && cachedData.aircontrol) {
          issuesFound.push({ period, issue: 'missing_energycontrol' });
        }
        
      } catch (error) {
        console.error(`Error parsing cache for ${key}:`, error);
      }
    }
  }
  
  //console.log(`Examined ${cacheEntries.length} cache entries, found ${issuesFound.length} issues`);
  
  // If there are issues, we need to check all period data to ensure 
  // we're not missing aircontrol or energycontrol data that exists in other entries
  if (issuesFound.length > 0) {
    console.warn("Cache issues found, attempting to repair...");
    
    // For each period with issues, check if data for that period exists in other entries
    for (const issue of issuesFound) {
      if (issue.issue === 'missing_aircontrol') {
        console.log(`Looking for aircontrol data for period ${issue.period}...`);
        
        // Look for matching aircontrol data in other cache entries
        for (const entry of cacheEntries) {
          if (entry.period !== issue.period && entry.data.aircontrol) {
            // Check if this aircontrol data belongs to our period
            if (entry.data.aircontrol.metadata && 
                entry.data.aircontrol.metadata.period === issue.period) {
              
              console.log(`Found matching aircontrol data for ${issue.period} in entry ${entry.period}`);
              
              // Get the current entry
              const currentData = cacheEntries.find(e => e.period === issue.period)?.data;
              if (currentData) {
                // Merge the data
                const mergedData = {
                  ...currentData,
                  aircontrol: entry.data.aircontrol
                };
                
                // Save back to sessionStorage
                sessionStorage.setItem(`monthlyData_${issue.period}`, JSON.stringify(mergedData));
                console.log(`Repaired cache for ${issue.period} by adding missing aircontrol data`);
              }
            }
          }
        }
      }
      // Similar logic for missing energycontrol if needed
    }
  }
  
  // Perform a final validation check
  let allFixed = true;
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key && key.startsWith('monthlyData_') && key !== 'monthlyData_lastCacheTime') {
      try {
        const period = key.replace('monthlyData_', '');
        const cachedData = JSON.parse(sessionStorage.getItem(key));
        
        if ((cachedData.energycontrol && !cachedData.aircontrol) || 
            (!cachedData.energycontrol && cachedData.aircontrol)) {
          console.warn(`Period ${period} still has incomplete data after repair`);
          allFixed = false;
        }
        
      } catch (error) {
        console.error(`Error parsing cache during validation for ${key}:`, error);
      }
    }
  }
  
  if (allFixed && issuesFound.length > 0) {
    console.log("All cache issues successfully repaired!");
  } else if (!allFixed) {
    console.warn("Some cache issues could not be automatically repaired.");
  } else {
    console.log("No cache issues found, all data is complete.");
  }
};




// Transform API response data into the format expected by components
const transformDataForComponents = (apiData) => {
  if (!apiData) {
    //console.log("No data to transform");
    return null;
  }
  
  console.log("Transforming data for components:", {
    hasEnergyControl: !!apiData.energycontrol,
    hasAirControl: !!apiData.aircontrol,
    energyControlKeys: apiData.energycontrol ? Object.keys(apiData.energycontrol) : [],
    airControlKeys: apiData.aircontrol ? Object.keys(apiData.aircontrol) : []
  });
  
  const result = {};
  
  // Process energycontrol data if it exists
  if (apiData.energycontrol) {
    // Check format - simple direct object
    if (apiData.energycontrol.metadata || 
        apiData.energycontrol.spot_comparison || 
        apiData.energycontrol.heating_summary || 
        apiData.energycontrol.consumption_summary) {
      result.energycontrol = apiData.energycontrol;
      //console.log("Direct energycontrol format detected");
    } 
    // Results with periods format
    else if (apiData.energycontrol.results) {
      const currentPeriodData = Object.entries(apiData.energycontrol.results)
        .find(([period, data]) => data.status === 'found' && data.data);
      
      if (currentPeriodData) {
        result.energycontrol = currentPeriodData[1].data;
      //  console.log(`Results format energycontrol detected for period ${currentPeriodData[0]}`);
      }
    }
    // Unknown format - log but still try to use it
    else {
      console.warn("Unknown energycontrol format:", Object.keys(apiData.energycontrol));
      result.energycontrol = apiData.energycontrol;
    }
  }
  
  // Process aircontrol data if it exists
  if (apiData.aircontrol) {
    // Check format - simple direct object
    if (apiData.aircontrol.co2 || 
        apiData.aircontrol.temperature || 
        apiData.aircontrol.humidity ||
        apiData.aircontrol.metadata) {
      result.aircontrol = apiData.aircontrol;
      console.log("Direct aircontrol format detected");
    } 
    // Results with periods format
    else if (apiData.aircontrol.results) {
      const currentPeriodData = Object.entries(apiData.aircontrol.results)
        .find(([period, data]) => data.status === 'found' && data.data);
      
      if (currentPeriodData) {
        result.aircontrol = currentPeriodData[1].data;
        console.log(`Results format aircontrol detected for period ${currentPeriodData[0]}`);
      }
    }
    // Unknown format - log but still try to use it
    else {
      console.warn("Unknown aircontrol format:", Object.keys(apiData.aircontrol));
      result.aircontrol = apiData.aircontrol;
    }
  }
  
  // Log the final result
  console.log("Transformed data:", {
    hasEnergyControl: !!result.energycontrol,
    hasAirControl: !!result.aircontrol,
    keys: Object.keys(result)
  });
  
  return Object.keys(result).length > 0 ? result : null;
};

// MonthlyDataManager component to handle all monthly data operations
const MonthlyDataManager = ({ userData, jwtToken, setAppData, setErrors, setLoading, effectiveAccountName }) => {
  // Use effectiveAccountName if provided (for admin viewing as another user), otherwise fall back to userData.accountName
  const accountName = effectiveAccountName || userData?.accountName;

  // Initialize with previous month if today is first day of month
  const getCurrentMonthOrPreviousIfFirstDay = () => {
    const today = new Date();
    if (today.getDate() === 1) {
      // If it's the first day of the month, default to previous month
      const prevDate = new Date(today);
      prevDate.setDate(0); // Last day of previous month
      return formatYearMonth(prevDate);
    } else {
      return formatYearMonth(today);
    }
  };

  const [currentMonth, setCurrentMonth] = useState(getCurrentMonthOrPreviousIfFirstDay());
  const [dataCache, setDataCache] = useState({});
  const [fetchedMonths, setFetchedMonths] = useState([]);
  const [hasMoreHistory, setHasMoreHistory] = useState(true);
  
  // Track if the current viewing month is the actual current month
  const isActualCurrentMonth = useCallback(() => {
    const today = new Date();
    return currentMonth === formatYearMonth(today);
  }, [currentMonth]);

  // Check if cache is expired (past 6am today)
  const isCacheExpired = () => {
    const now = new Date();
    const sixAM = new Date(now);
    sixAM.setHours(6, 0, 0, 0);
    
    // Get the last cache time from sessionStorage
    const lastCacheTimeStr = sessionStorage.getItem('monthlyData_lastCacheTime');
    if (!lastCacheTimeStr) return true;
    
    const lastCacheTime = new Date(lastCacheTimeStr);
    
    // If it's past 6am and the last cache time was before 6am today, the cache is expired
    return now >= sixAM && lastCacheTime < sixAM;
  };
  
  // Function to save data to cache with proper merging
// Enhanced saveToCache function with safeguards against data loss
const saveToCache = useCallback((period, newData) => {
  if (!period || !newData) {
    console.warn(`Attempted to cache data without period or data:`, { period, hasData: !!newData });
    return;
  }
  
  try {
    console.log(`Saving to cache for ${period}:`, {
      hasEnergyControl: !!newData.energycontrol,
      hasAirControl: !!newData.aircontrol,
    });
    
    // CRITICAL STEP: Always check for existing data first
    let existingData = null;
    try {
      // Check sessionStorage
      const existingStr = sessionStorage.getItem(`monthlyData_${period}`);
      if (existingStr) {
        existingData = JSON.parse(existingStr);
        console.log(`Found existing sessionStorage cache for ${period}:`, {
          hasEnergyControl: !!existingData.energycontrol,
          hasAirControl: !!existingData.aircontrol,
        });
      }
      
      // If nothing in sessionStorage, check in-memory cache
      if (!existingData && dataCache[period]) {
        existingData = dataCache[period];
        console.log(`Found existing in-memory cache for ${period}:`, {
          hasEnergyControl: !!existingData.energycontrol,
          hasAirControl: !!existingData.aircontrol,
        });
      }
    } catch (err) {
      console.error(`Error reading existing cache for ${period}:`, err);
    }
    
    // NEVER OVERWRITE - Create properly merged data object
    let mergedData;
    
    if (existingData) {
      // Start with a complete copy of existing data
      mergedData = JSON.parse(JSON.stringify(existingData));
      
      // Only update components that are present in the new data
      if (newData.energycontrol) {
        mergedData.energycontrol = newData.energycontrol;
      }
      
      if (newData.aircontrol) {
        mergedData.aircontrol = newData.aircontrol;
      }
      
      console.log(`Merged with existing data for ${period}:`, {
        hadEnergyControl: !!existingData.energycontrol,
        hadAirControl: !!existingData.aircontrol,
        nowHasEnergyControl: !!mergedData.energycontrol,
        nowHasAirControl: !!mergedData.aircontrol,
      });
    } else {
      // No existing data, just use the new data
      mergedData = { ...newData };
    }
    
    // Save to sessionStorage
    sessionStorage.setItem(`monthlyData_${period}`, JSON.stringify(mergedData));
    
    // Update the cache timestamp
    sessionStorage.setItem('monthlyData_lastCacheTime', new Date().toISOString());
    
    // Update the in-memory cache
    setDataCache(prevCache => {
      // Double check if in-memory cache has data that's not in our merged data
      const inMemoryData = prevCache[period];
      if (inMemoryData) {
        if (inMemoryData.aircontrol && !mergedData.aircontrol) {
          console.log(`Preserving in-memory aircontrol data for ${period} that wasn't in merged data`);
          mergedData.aircontrol = inMemoryData.aircontrol;
        }
        if (inMemoryData.energycontrol && !mergedData.energycontrol) {
          console.log(`Preserving in-memory energycontrol data for ${period} that wasn't in merged data`);
          mergedData.energycontrol = inMemoryData.energycontrol;
        }
      }
      
      return {
        ...prevCache,
        [period]: mergedData
      };
    });
    
    console.log(`Successfully saved data for ${period} to cache:`, {
      finalHasEnergyControl: !!mergedData.energycontrol,
      finalHasAirControl: !!mergedData.aircontrol,
    });
  } catch (error) {
    console.error(`Error saving to cache for ${period}:`, error);
  }
}, [dataCache, setDataCache]);
  
// 5. Finally, enhance the processAndCacheAllPeriods function to better handle data merging
const processAndCacheAllPeriods = useCallback((responseData) => {
  if (!responseData) {
    console.log("No response data to process");
    return;
  }
  
  console.log("[PROCESSING] Starting to process API response:", {
    hasEnergyControl: !!responseData.energycontrol,
    hasAirControl: !!responseData.aircontrol
  });
  
  // Snapshot the current cache state
  const cacheSnapshot = {};
  Object.entries(dataCache).forEach(([period, data]) => {
    cacheSnapshot[period] = JSON.parse(JSON.stringify(data));
  });
  
  // Create a set of all periods to process
  const allPeriods = new Set();
  
  // Add energycontrol periods
  if (responseData.energycontrol && responseData.energycontrol.results) {
    Object.keys(responseData.energycontrol.results).forEach(period => {
      allPeriods.add(period);
    });
  }
  
  // Add aircontrol periods
  if (responseData.aircontrol && responseData.aircontrol.results) {
    Object.keys(responseData.aircontrol.results).forEach(period => {
      allPeriods.add(period);
    });
  }
  
  console.log(`[PROCESSING] Found ${allPeriods.size} periods to process`);
  
  // Process each period, preserving existing data
  allPeriods.forEach(period => {
    console.log(`[PROCESSING] Processing data for ${period}`);
    
    // Get existing data for this period
    const existingData = cacheSnapshot[period] || {};
    
    // Create a container for new data while preserving existing data
    const newData = { ...existingData };
    let dataChanged = false;
    
    // Process energycontrol data for this period
    if (responseData.energycontrol && 
        responseData.energycontrol.results && 
        responseData.energycontrol.results[period] && 
        responseData.energycontrol.results[period].status === 'found' && 
        responseData.energycontrol.results[period].data) {
      
      newData.energycontrol = responseData.energycontrol.results[period].data;
      dataChanged = true;
      console.log(`[PROCESSING] Added energycontrol data for ${period}`);
    }
    
    // Process aircontrol data for this period
    if (responseData.aircontrol && 
        responseData.aircontrol.results && 
        responseData.aircontrol.results[period] && 
        responseData.aircontrol.results[period].status === 'found' && 
        responseData.aircontrol.results[period].data) {
      
      newData.aircontrol = responseData.aircontrol.results[period].data;
      dataChanged = true;
      console.log(`[PROCESSING] Added aircontrol data for ${period}`);
    }
    
    // Only update cache if something changed
    if (dataChanged) {
      // Transform the data
      const transformedData = transformDataForComponents(newData);
      
      if (transformedData) {
        // Preserve any existing components not in the transformed data
        if (existingData.energycontrol && !transformedData.energycontrol) {
          transformedData.energycontrol = existingData.energycontrol;
          console.log(`[PROCESSING] Preserved existing energycontrol data for ${period}`);
        }
        
        if (existingData.aircontrol && !transformedData.aircontrol) {
          transformedData.aircontrol = existingData.aircontrol;
          console.log(`[PROCESSING] Preserved existing aircontrol data for ${period}`);
        }
        
        // Save to cache
        console.log(`[PROCESSING] Saving data for ${period}:`, {
          hasEnergyControl: !!transformedData.energycontrol,
          hasAirControl: !!transformedData.aircontrol
        });
        
        // Update sessionStorage
        try {
          sessionStorage.setItem(`monthlyData_${period}`, JSON.stringify(transformedData));
          sessionStorage.setItem('monthlyData_lastCacheTime', new Date().toISOString());
        } catch (err) {
          console.error(`[PROCESSING] Error saving to sessionStorage:`, err);
        }
        
        // Update in-memory cache
        setDataCache(prev => ({
          ...prev,
          [period]: transformedData
        }));
        
        setFetchedMonths(prev => [...new Set([...prev, period])]);
      }
    }
  });
  
  // Verify the cache state after processing
  setTimeout(() => {
    console.log("[PROCESSING] Verifying cache after processing:");
    
    allPeriods.forEach(period => {
      const before = cacheSnapshot[period] || {};
      const after = dataCache[period] || {};
      
      console.log(`[PROCESSING] Cache for ${period}:`, {
        hadEnergyControl: !!before.energycontrol,
        hadAirControl: !!before.aircontrol,
        nowHasEnergyControl: !!after.energycontrol,
        nowHasAirControl: !!after.aircontrol
      });
      
      // Check for data loss
      if (before.aircontrol && !after.aircontrol) {
        console.warn(`[PROCESSING] Aircontrol data was lost for ${period}, restoring`);
        
        // Restore the data
        setDataCache(prev => ({
          ...prev,
          [period]: {
            ...prev[period],
            aircontrol: before.aircontrol
          }
        }));
        
        // Update sessionStorage
        try {
          const currentStr = sessionStorage.getItem(`monthlyData_${period}`);
          if (currentStr) {
            const current = JSON.parse(currentStr);
            const fixed = {
              ...current,
              aircontrol: before.aircontrol
            };
            sessionStorage.setItem(`monthlyData_${period}`, JSON.stringify(fixed));
          }
        } catch (err) {
          console.error(`[PROCESSING] Error fixing sessionStorage:`, err);
        }
      }
      
      // Similarly for energycontrol
      if (before.energycontrol && !after.energycontrol) {
        console.warn(`[PROCESSING] Energycontrol data was lost for ${period}, restoring`);
        
        // Restore the data
        setDataCache(prev => ({
          ...prev,
          [period]: {
            ...prev[period],
            energycontrol: before.energycontrol
          }
        }));
        
        // Update sessionStorage
        try {
          const currentStr = sessionStorage.getItem(`monthlyData_${period}`);
          if (currentStr) {
            const current = JSON.parse(currentStr);
            const fixed = {
              ...current,
              energycontrol: before.energycontrol
            };
            sessionStorage.setItem(`monthlyData_${period}`, JSON.stringify(fixed));
          }
        } catch (err) {
          console.error(`[PROCESSING] Error fixing sessionStorage:`, err);
        }
      }
    });
  }, 250);
}, [dataCache, transformDataForComponents, setDataCache, setFetchedMonths]);
  
  
  // Function to check if current month's data exists and is valid
  const hasValidCurrentMonthData = useCallback((monthToCheck) => {
    const monthData = dataCache[monthToCheck];
    return monthData && 
      (monthData.energycontrol || monthData.aircontrol) &&
      !(monthData.energycontrol && 
        monthData.energycontrol.aggregate_status === 'none_found');
  }, [dataCache]);
  
  // Check if previous month data is available in cache
  const checkPreviousMonthAvailability = useCallback(() => {
    const prevMonth = getPreviousMonth(currentMonth);
    
    // Check if previous month has valid data in the cache
    const prevMonthData = dataCache[prevMonth];
    
    // Check if the previous month data is valid (has content and not an error state)
    const hasValidData = prevMonthData && 
      (prevMonthData.energycontrol || prevMonthData.aircontrol) &&
      !(prevMonthData.energycontrol && 
        prevMonthData.energycontrol.aggregate_status === 'none_found');
    
    console.log(`Previous month ${prevMonth} has valid data: ${hasValidData}`);
    setHasMoreHistory(hasValidData);
    return hasValidData;
  }, [currentMonth, dataCache]);

  // Debug and fix the cache when needed
  const repairCache = useCallback(() => {
    console.log("Repairing cache...");
    
    const fixedCache = {...dataCache};
    let fixed = false;
    
    // Look for any periods that may have aircontrol in one entry but not another
    Object.entries(dataCache).forEach(([period, data]) => {
      // Log the current state
      inspectData(`Cache entry for ${period}`, data);
      
      // If we detect a problem, fix it
      if (data.energycontrol && !data.aircontrol) {
        console.warn(`Period ${period} is missing aircontrol data, looking for a fix...`);
        
        // Try to find aircontrol data in another entry
        Object.entries(dataCache).forEach(([otherPeriod, otherData]) => {
          if (otherPeriod !== period && otherData.aircontrol) {
            console.log(`Found aircontrol data in ${otherPeriod}, checking if it might belong to ${period}...`);
            
            // If the aircontrol data has metadata with our period, it belongs to us
            if (otherData.aircontrol.metadata && otherData.aircontrol.metadata.period === period) {
              console.log(`Match found! Fixing cache for ${period} by adding aircontrol data from ${otherPeriod}`);
              
              // Fix the cache entry
              fixedCache[period] = {
                ...data,
                aircontrol: otherData.aircontrol
              };
              
              fixed = true;
            }
          }
        });
      }
    });
    
    // If we fixed anything, update the cache
    if (fixed) {
      console.log("Cache repaired, updating...");
      
      // Update in-memory cache
      setDataCache(fixedCache);
      
      // Update sessionStorage
      Object.entries(fixedCache).forEach(([period, data]) => {
        sessionStorage.setItem(`monthlyData_${period}`, JSON.stringify(data));
      });
      
      console.log("Cache repair complete.");
    } else {
      console.log("No cache repairs needed.");
    }
  }, [dataCache]);
  
  // Similar fix for fetchHistoricalData to prevent data loss
const fetchHistoricalData = useCallback(async (monthToFetch) => {
  if (!userData || !jwtToken) return;
  
  console.log(`Fetching historical data for month: ${monthToFetch}`);
  
  // IMPORTANT: Snapshot current data before any network requests
  const currentDataSnapshot = { ...dataCache };
  
  try {
    const requestBody = {
      account_name: accountName,
      action: 'fetch',
      months: 2, // Request current and previous month
      period: monthToFetch,
      include_air: true // ALWAYS include air
    };
    
    const response = await fetch('https://j1jjasmb97.execute-api.eu-north-1.amazonaws.com/MonthlyReport-API', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${jwtToken}`
      },
      body: JSON.stringify(requestBody),
    });
    
    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }
    
    const responseData = await response.json();
    console.log("Historical data API response:", {
      hasEnergyControl: !!responseData.energycontrol,
      hasAirControl: !!responseData.aircontrol,
    });
    
    if (responseData) {
      console.log("Processing historical data API response");
      
      // Process all periods in the response
      processAndCacheAllPeriods(responseData);
      
      // Check for data loss after processing
      Object.keys(currentDataSnapshot).forEach(period => {
        const prevData = currentDataSnapshot[period];
        const currData = dataCache[period];
        
        if (prevData && currData) {
          // Check if we lost aircontrol
          if (prevData.aircontrol && !currData.aircontrol) {
            console.warn(`Lost aircontrol data for ${period} during historical data processing, restoring it`);
            
            // Restore the missing aircontrol data
            setDataCache(prev => ({
              ...prev,
              [period]: {
                ...prev[period],
                aircontrol: prevData.aircontrol
              }
            }));
            
            // Update sessionStorage too
            try {
              const existingStr = sessionStorage.getItem(`monthlyData_${period}`);
              if (existingStr) {
                const existingData = JSON.parse(existingStr);
                const fixedData = {
                  ...existingData,
                  aircontrol: prevData.aircontrol
                };
                sessionStorage.setItem(`monthlyData_${period}`, JSON.stringify(fixedData));
              }
            } catch (err) {
              console.error(`Error restoring aircontrol data in sessionStorage:`, err);
            }
          }
          
          // Check if we lost energycontrol
          if (prevData.energycontrol && !currData.energycontrol) {
            console.warn(`Lost energycontrol data for ${period} during historical data processing, restoring it`);
            
            // Restore the missing energycontrol data
            setDataCache(prev => ({
              ...prev,
              [period]: {
                ...prev[period],
                energycontrol: prevData.energycontrol
              }
            }));
            
            // Update sessionStorage too
            try {
              const existingStr = sessionStorage.getItem(`monthlyData_${period}`);
              if (existingStr) {
                const existingData = JSON.parse(existingStr);
                const fixedData = {
                  ...existingData,
                  energycontrol: prevData.energycontrol
                };
                sessionStorage.setItem(`monthlyData_${period}`, JSON.stringify(fixedData));
              }
            } catch (err) {
              console.error(`Error restoring energycontrol data in sessionStorage:`, err);
            }
          }
        }
      });
    } else {
      console.log("No valid historical data found in API response");
    }
  } catch (error) {
    console.error('Error fetching historical data:', error);
  }
}, [userData, jwtToken, dataCache, processAndCacheAllPeriods, setDataCache]);

// Add a specialized data integrity checker
const checkDataIntegrity = useCallback(() => {
  console.log("Checking data integrity across all cached periods...");
  
  // Read directly from sessionStorage to avoid in-memory cache issues
  const storedPeriods = [];
  
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key && key.startsWith('monthlyData_') && key !== 'monthlyData_lastCacheTime') {
      try {
        const period = key.replace('monthlyData_', '');
        const cachedData = JSON.parse(sessionStorage.getItem(key));
        
        storedPeriods.push({
          period,
          hasEnergyControl: !!cachedData.energycontrol,
          hasAirControl: !!cachedData.aircontrol
        });
        
        // Update in-memory cache to match sessionStorage
        const inMemoryData = dataCache[period];
        let needsUpdate = false;
        
        if (!inMemoryData) {
          needsUpdate = true;
        } else {
          if (cachedData.energycontrol && !inMemoryData.energycontrol) {
            needsUpdate = true;
          }
          if (cachedData.aircontrol && !inMemoryData.aircontrol) {
            needsUpdate = true;
          }
        }
        
        if (needsUpdate) {
          console.log(`Updating in-memory cache for ${period} to match sessionStorage`);
          setDataCache(prev => ({
            ...prev,
            [period]: cachedData
          }));
        }
      } catch (error) {
        console.error(`Error checking data integrity for ${key}:`, error);
      }
    }
  }
  
  console.log("Cache integrity check results:", storedPeriods);
  
  // Check for missing components
  const issuesFound = storedPeriods.filter(p => p.hasEnergyControl !== p.hasAirControl);
  if (issuesFound.length > 0) {
    console.warn(`Found ${issuesFound.length} periods with missing components:`, issuesFound);
  } else {
    console.log("All periods have consistent data components.");
  }
}, [dataCache, setDataCache]);

  
  // Enhanced fetchMonthlyData with data preservation safeguards
  const fetchMonthlyData = useCallback(async () => {
    if (!userData || !jwtToken) return;
    
    setLoading(prev => ({ ...prev, monthly: true }));
    setErrors(prev => ({ ...prev, monthly: null }));
    
    try {
      // CRITICAL: Take a snapshot of the entire cache before any operations
      const snapshotBeforeFetch = {};
      
      // Create a deep copy of existing cache data to ensure we don't lose anything
      Object.entries(dataCache).forEach(([period, data]) => {
        snapshotBeforeFetch[period] = JSON.parse(JSON.stringify(data));
      });
      
      console.log(`[SNAPSHOT] Cache state before fetch for ${currentMonth}:`, 
        Object.keys(snapshotBeforeFetch).map(period => ({
          period,
          hasEnergyControl: !!snapshotBeforeFetch[period]?.energycontrol,
          hasAirControl: !!snapshotBeforeFetch[period]?.aircontrol
        }))
      );
      
      // If we already have data for the current month, use it immediately
      if (dataCache[currentMonth]) {
        console.log(`Using cached data for ${currentMonth}:`, {
          hasEnergyControl: !!dataCache[currentMonth].energycontrol,
          hasAirControl: !!dataCache[currentMonth].aircontrol
        });
        
        setAppData(prev => ({
          ...prev,
          monthly: dataCache[currentMonth]
        }));
        
        setLoading(prev => ({ ...prev, monthly: false }));
        checkPreviousMonthAvailability();
        return;
      }
      
      console.log(`Fetching monthly data for ${currentMonth}`);
      
      const requestBody = {
        account_name: accountName,
        action: 'fetch',
        months: 2,
        period: currentMonth,
        include_air: true
      };
      
      const response = await fetch('https://j1jjasmb97.execute-api.eu-north-1.amazonaws.com/MonthlyReport-API', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`
        },
        body: JSON.stringify(requestBody),
      });
      
      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }
      
      const responseData = await response.json();
      console.log("API response:", responseData);
      
      if (responseData) {
        console.log("Processing API response for monthly data");
        
        // Process all periods in the response
        processAndCacheAllPeriods(responseData);
        
        // CRITICAL FIX: Check if any data was lost during processing
        setTimeout(() => {
          console.log("[DATA PRESERVATION] Checking for data loss after fetch");
          Object.keys(snapshotBeforeFetch).forEach(period => {
            const before = snapshotBeforeFetch[period];
            const after = dataCache[period];
            
            if (before && after) {
              // Check if we lost aircontrol data
              if (before.aircontrol && !after.aircontrol) {
                console.warn(`[DATA PRESERVATION] Aircontrol data was lost for ${period}, restoring it`);
                
                // Update in-memory cache
                setDataCache(prev => ({
                  ...prev,
                  [period]: {
                    ...prev[period],
                    aircontrol: before.aircontrol
                  }
                }));
                
                // Update sessionStorage
                try {
                  const existingStr = sessionStorage.getItem(`monthlyData_${period}`);
                  if (existingStr) {
                    const existingData = JSON.parse(existingStr);
                    const fixedData = {
                      ...existingData,
                      aircontrol: before.aircontrol
                    };
                    
                    sessionStorage.setItem(`monthlyData_${period}`, JSON.stringify(fixedData));
                    console.log(`[DATA PRESERVATION] Restored aircontrol data for ${period} in sessionStorage`);
                  }
                } catch (err) {
                  console.error(`Error restoring data for ${period}:`, err);
                }
              }
              
              // Check if we lost energycontrol data
              if (before.energycontrol && !after.energycontrol) {
                console.warn(`[DATA PRESERVATION] Energycontrol data was lost for ${period}, restoring it`);
                
                // Update in-memory cache
                setDataCache(prev => ({
                  ...prev,
                  [period]: {
                    ...prev[period],
                    energycontrol: before.energycontrol
                  }
                }));
                
                // Update sessionStorage
                try {
                  const existingStr = sessionStorage.getItem(`monthlyData_${period}`);
                  if (existingStr) {
                    const existingData = JSON.parse(existingStr);
                    const fixedData = {
                      ...existingData,
                      energycontrol: before.energycontrol
                    };
                    
                    sessionStorage.setItem(`monthlyData_${period}`, JSON.stringify(fixedData));
                    console.log(`[DATA PRESERVATION] Restored energycontrol data for ${period} in sessionStorage`);
                  }
                } catch (err) {
                  console.error(`Error restoring data for ${period}:`, err);
                }
              }
            }
          });
        }, 250);
        
        // After processing (and potential fixes), set the app data
        if (dataCache[currentMonth]) {
          console.log(`Setting app data for ${currentMonth}:`, {
            hasEnergyControl: !!dataCache[currentMonth]?.energycontrol,
            hasAirControl: !!dataCache[currentMonth]?.aircontrol
          });
          
          setAppData(prev => ({
            ...prev,
            monthly: dataCache[currentMonth]
          }));
        } else {
          // Rest of existing code for handling missing data...
        }
      } else {
        console.error("Invalid API response format");
        setErrors(prev => ({
          ...prev,
          monthly: `Failed to fetch data: Invalid response format`
        }));
      }
    } catch (error) {
      console.error('Error fetching monthly data:', error);
      setErrors(prev => ({
        ...prev,
        monthly: `Failed to fetch data: ${error.message}`
      }));
    } finally {
      setLoading(prev => ({ ...prev, monthly: false }));
      checkPreviousMonthAvailability();
    }
  }, [
    userData,
    jwtToken,
    currentMonth,
    dataCache,
    processAndCacheAllPeriods,
    setAppData,
    setErrors,
    setLoading,
    checkPreviousMonthAvailability
  ]);
  
  // Initialize the cache from sessionStorage on component mount
  useEffect(() => {
    // Check if cache should be cleared based on time
    if (isCacheExpired()) {
      console.log('Cache expired (past 6am), clearing cache');
      
      // Clear all monthly data items from sessionStorage
      const keysToRemove = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith('monthlyData_') && key !== 'monthlyData_lastCacheTime') {
          keysToRemove.push(key);
        }
      }
      
      // Remove the identified keys
      keysToRemove.forEach(key => sessionStorage.removeItem(key));
      
      // Set an empty cache
      setDataCache({});
      return;
    }

    // After loading the cache, check for any missing aircontrol data
    const initialCache = {}; 
    // Find all monthly data items in sessionStorage
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith('monthlyData_') && key !== 'monthlyData_lastCacheTime') {
        try {
          const period = key.replace('monthlyData_', '');
          const cachedData = JSON.parse(sessionStorage.getItem(key));
          
          // Log each cache entry we load
          inspectData(`Loaded from sessionStorage for period ${period}`, cachedData);
          
          initialCache[period] = cachedData;
        } catch (error) {
          console.error('Error parsing cached data:', error);
          sessionStorage.removeItem(key);
        }
      }
    }
    
    // Check for any partial cache entries (missing energycontrol or aircontrol)
    Object.entries(initialCache).forEach(([period, data]) => {
      // If we have energycontrol but not aircontrol, or vice versa, this might be a problem
      if ((data.energycontrol && !data.aircontrol) || (!data.energycontrol && data.aircontrol)) {
        console.warn(`Period ${period} has incomplete data (missing components):`, 
          {hasEnergyControl: !!data.energycontrol, hasAirControl: !!data.aircontrol});
      }
    });
    
    setDataCache(initialCache);
  }, []);
  
  // Add this useEffect to run the repair once on component load
  useEffect(() => {
    // Only run once after cache is loaded
    if (Object.keys(dataCache).length > 0) {
      repairCache();
    }
  }, [dataCache, repairCache]);
  
// 2. Second critical fix - enhance the month navigation with data preservation
const onPreviousMonth = useCallback(() => {
  const prevMonth = getPreviousMonth(currentMonth);
  
  // CRITICAL: Take a complete snapshot of the current cache state
  const cacheBefore = {};
  
  Object.entries(dataCache).forEach(([period, data]) => {
    // Deep copy to avoid reference issues
    cacheBefore[period] = JSON.parse(JSON.stringify(data));
  });
  
  //console.log(`[MONTH NAVIGATION] Moving from ${currentMonth} to ${prevMonth}`);
  
  // Set the new month and use cached data if available
  setCurrentMonth(prevMonth);
  
  if (dataCache[prevMonth]) {
    console.log(`Using cached data for ${prevMonth}`);
    setAppData(prev => ({
      ...prev,
      monthly: dataCache[prevMonth]
    }));
  }
  
  // Background fetch for previous months if needed
  const prevPrevMonth = getPreviousMonth(prevMonth);
  const needsHistoricalData = !dataCache[prevPrevMonth];
  
  if (needsHistoricalData) {
    setTimeout(() => {
      fetchHistoricalData(prevMonth);
    }, 100);
  }
  
  // CRITICAL: Monitor for any data loss after the month change
  setTimeout(() => {
    //console.log("[MONTH NAVIGATION] Checking data integrity after month change");
    
    Object.keys(cacheBefore).forEach(period => {
      const before = cacheBefore[period];
      const after = dataCache[period];
      
      if (before && after) {
        if (before.aircontrol && !after.aircontrol) {
          console.warn(`[MONTH NAVIGATION] Aircontrol data lost for ${period} during month navigation`);
          
          // Restore the lost data
          setDataCache(prev => ({
            ...prev,
            [period]: {
              ...prev[period],
              aircontrol: before.aircontrol
            }
          }));
          
          // Update sessionStorage too
          try {
            const storageData = JSON.parse(sessionStorage.getItem(`monthlyData_${period}`) || '{}');
            storageData.aircontrol = before.aircontrol;
            sessionStorage.setItem(`monthlyData_${period}`, JSON.stringify(storageData));
          } catch (err) {
            console.error("Error updating sessionStorage:", err);
          }
        }
        
        // Similarly check for energycontrol loss
        if (before.energycontrol && !after.energycontrol) {
          console.warn(`[MONTH NAVIGATION] Energycontrol data lost for ${period} during month navigation`);
          
          // Restore the lost data
          setDataCache(prev => ({
            ...prev,
            [period]: {
              ...prev[period],
              energycontrol: before.energycontrol
            }
          }));
          
          // Update sessionStorage too
          try {
            const storageData = JSON.parse(sessionStorage.getItem(`monthlyData_${period}`) || '{}');
            storageData.energycontrol = before.energycontrol;
            sessionStorage.setItem(`monthlyData_${period}`, JSON.stringify(storageData));
          } catch (err) {
            console.error("Error updating sessionStorage:", err);
          }
        }
      }
    });
    
    // After fixing any issues, make sure the app data is up to date
    if (dataCache[prevMonth] && 
        (dataCache[prevMonth].energycontrol || dataCache[prevMonth].aircontrol)) {
      setAppData(prev => ({
        ...prev,
        monthly: dataCache[prevMonth]
      }));
    }
  }, 300);
}, [
  currentMonth, 
  dataCache, 
  setCurrentMonth, 
  setAppData, 
  fetchHistoricalData
]);

// 3. Similarly enhance onNextMonth with data preservation
const onNextMonth = useCallback(() => {
  const nextMonth = getNextMonth(currentMonth);
  
  // CRITICAL: Take a snapshot of current cache
  const cacheBefore = {};
  
  Object.entries(dataCache).forEach(([period, data]) => {
    cacheBefore[period] = JSON.parse(JSON.stringify(data));
  });
  
  //console.log(`[MONTH NAVIGATION] Moving from ${currentMonth} to ${nextMonth}`);
  
  // Check if we can navigate to next month
  const currentDate = new Date();
  const currentMonthStr = formatYearMonth(currentDate);
  
  if (nextMonth > currentMonthStr) {
    console.log("Cannot go beyond current month");
    setErrors(prev => ({
      ...prev,
      monthly: "Future data is not available"
    }));
    return;
  }
  
  // Check for first day of month issues
  const isFirstDay = currentDate.getDate() === 1;
  if (isFirstDay && nextMonth === currentMonthStr) {
    const hasCurrentData = hasValidCurrentMonthData(nextMonth);
    if (!hasCurrentData) {
      console.log("First day of month with no data for current month");
      setErrors(prev => ({
        ...prev,
        monthly: "Data for current month is not yet available"
      }));
      return;
    }
  }
  
  // Update the month
  setCurrentMonth(nextMonth);
  
  // Use cached data if available
  if (dataCache[nextMonth]) {
    console.log(`Using cached data for ${nextMonth}`);
    setAppData(prev => ({
      ...prev,
      monthly: dataCache[nextMonth]
    }));
  }
  
  // CRITICAL: Check for data integrity after month change
  setTimeout(() => {
    //console.log("[MONTH NAVIGATION] Checking data integrity after next month navigation");
    
    Object.keys(cacheBefore).forEach(period => {
      const before = cacheBefore[period];
      const after = dataCache[period];
      
      if (before && after) {
        if (before.aircontrol && !after.aircontrol) {
          console.warn(`[MONTH NAVIGATION] Aircontrol data lost for ${period} during next month navigation`);
          
          // Restore the lost data
          setDataCache(prev => ({
            ...prev,
            [period]: {
              ...prev[period],
              aircontrol: before.aircontrol
            }
          }));
          
          // Update sessionStorage too
          try {
            const storageData = JSON.parse(sessionStorage.getItem(`monthlyData_${period}`) || '{}');
            storageData.aircontrol = before.aircontrol;
            sessionStorage.setItem(`monthlyData_${period}`, JSON.stringify(storageData));
          } catch (err) {
            console.error("Error updating sessionStorage:", err);
          }
        }
        
        // Similarly check for energycontrol loss
        if (before.energycontrol && !after.energycontrol) {
          console.warn(`[MONTH NAVIGATION] Energycontrol data lost for ${period} during next month navigation`);
          
          // Restore the lost data
          setDataCache(prev => ({
            ...prev,
            [period]: {
              ...prev[period],
              energycontrol: before.energycontrol
            }
          }));
          
          // Update sessionStorage too
          try {
            const storageData = JSON.parse(sessionStorage.getItem(`monthlyData_${period}`) || '{}');
            storageData.energycontrol = before.energycontrol;
            sessionStorage.setItem(`monthlyData_${period}`, JSON.stringify(storageData));
          } catch (err) {
            console.error("Error updating sessionStorage:", err);
          }
        }
      }
    });
    
    // After fixing any issues, make sure the app data is up to date
    if (dataCache[nextMonth] && 
        (dataCache[nextMonth].energycontrol || dataCache[nextMonth].aircontrol)) {
      setAppData(prev => ({
        ...prev,
        monthly: dataCache[nextMonth]
      }));
    }
  }, 300);
}, [
  currentMonth,
  dataCache,
  hasValidCurrentMonthData,
  setCurrentMonth,
  setAppData,
  setErrors
]);

// This will ensure that any existing cache is properly saved and formatted
useEffect(() => {
  const initializeCache = () => {
    console.log("[INITIALIZATION] Checking cache integrity on mount");
    
    // Check sessionStorage for any data
    const periods = new Set();
    
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith('monthlyData_') && key !== 'monthlyData_lastCacheTime') {
        const period = key.replace('monthlyData_', '');
        periods.add(period);
      }
    }
    
    //console.log(`[INITIALIZATION] Found ${periods.size} periods in cache`);
    
    // Process each period
    periods.forEach(period => {
      try {
        const dataStr = sessionStorage.getItem(`monthlyData_${period}`);
        if (dataStr) {
          const data = JSON.parse(dataStr);
          console.log(`[INITIALIZATION] Loaded data for ${period}:`, {
            hasEnergyControl: !!data.energycontrol,
            hasAirControl: !!data.aircontrol
          });
          
          // Update in-memory cache
          setDataCache(prev => ({
            ...prev,
            [period]: data
          }));
        }
      } catch (err) {
        console.error(`[INITIALIZATION] Error loading data for ${period}:`, err);
      }
    });
  };
  
  initializeCache();
}, [setDataCache]);

  // Fetch data when currentMonth changes or when component mounts
  useEffect(() => {
    if (userData && jwtToken) {
      console.log("Triggering monthly data fetch for month:", currentMonth);
      fetchMonthlyData();
    }
  }, [userData, jwtToken, currentMonth, fetchMonthlyData]);
  
  // Check previous month availability on mount and when cache changes
  useEffect(() => {
    checkPreviousMonthAvailability();
  }, [currentMonth, dataCache, checkPreviousMonthAvailability]);

  // Add a new useEffect to run integrity check periodically
useEffect(() => {
  // Run on component mount
  checkDataIntegrity();
  
  // Also run when switching months
  const checkTimer = setTimeout(() => {
    checkDataIntegrity();
  }, 500);
  
  return () => clearTimeout(checkTimer);
}, [currentMonth, checkDataIntegrity]);

    // Call this function when the app first loads, before any API calls
  useEffect(() => {
    initializeApp();
  }, []);

  return {
    currentMonth,
    onPreviousMonth,
    onNextMonth,
    fetchMonthlyData,
    dataCache,
    isActualCurrentMonth: isActualCurrentMonth(),
    hasMoreHistory,
    // Add a new flag to indicate if next month has data
    hasNextMonthData: (() => {
      const nextMonth = getNextMonth(currentMonth);
      const currentDate = new Date();
      const currentMonthStr = formatYearMonth(currentDate);
      
      // First, check if next month is beyond current month
      if (nextMonth > currentMonthStr) {
        return false; // Future months never have data
      }
      
      // Check if we have valid data for the next month
      return hasValidCurrentMonthData(nextMonth);
    })()
  };
};

export default MonthlyDataManager;